import React, { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import ContentCard from "../../../components/misc/ContentCard";
import Dropdown, { DropdownValue } from "../../../components/misc/Dropdown";
import { getInternodeRoutes } from "../../../redux/api/expedition.api";
import {
  fetchCargoCarPickupCargoInternodeRoute,
  fetchNodesByInternodRoute,
} from "../../../redux/api/raports.api";
import CustomButton from "../../../components/CustomButton";
import ActionMenu from "../../../components/actionMenu/ActionMenu";
import LoadingIndicator from "../../../components/misc/LoadingIndicator";
import PackagesTable from "./components/PackagesTable";
import BinsTable from "./components/BinsTable";

import { clearPickupCargoInternodeRoute } from "../../../redux/reducer/raports.reducer";
import ClosePackageCargoInternodeModal from "./components/ClosePackageCargoInternodeModal";

function PickupCargoInternodeRoute() {
  const dispatch = useAppDispatch();

  const {
    raports: {
      pickupCargoInternodeRoute: {
        data: pickupCargoInternodeRouteData,
        isLoading: isLoadingPickupCargoInternodeRoute,
      },

      nodesByInternodRoute: {
        data: { data: nodesByInternodRoute },
      },
    },
    expedition: {
      internodeRoutes: {
        data: { data: internodeRoutes },
      },
    },
  } = useAppSelector((state) => ({
    raports: state.raports,
    expedition: state.expedition,
  }));

  const [selectedInternodeRoute, setSelectedInternodeRoute] =
    useState<DropdownValue | null>(null);
  const [selectedCurrentNode, setSelectedCurrentNode] =
    useState<DropdownValue | null>(null);
  const [selectedStartNode, setSelectedStartNode] =
    useState<DropdownValue | null>(null);
  const [selectedDestinationNode, setSelectedDestinationNode] =
    useState<DropdownValue | null>(null);

  const [activeCard, setActiveCard] = useState<string | number | null>(null);

  useEffect(() => {
    dispatch(
      getInternodeRoutes({
        page: 1,
        perPage: 1000,
      }),
    );

    dispatch(
      fetchNodesByInternodRoute({
        id_ruta_intranod: selectedInternodeRoute?.id,
      }),
    );
  }, [dispatch, selectedInternodeRoute?.id]);

  useEffect(() => {
    return () => {
      dispatch(clearPickupCargoInternodeRoute());
    };
  }, [dispatch]);

  const nodesByInternodRouteList = useMemo(
    () =>
      nodesByInternodRoute?.map((item) => ({
        label: item.nodNume,
        id: item.id_nod,
      })),
    [nodesByInternodRoute],
  );

  const internodeRouteNodesList = useMemo(
    () =>
      internodeRoutes?.map((item) => ({
        label: item.denumire,
        id: item.id_ruta_intranod,
      })),
    [internodeRoutes],
  );

  const handleSearch = () => {
    setActiveCard(null);

    if (
      selectedInternodeRoute &&
      selectedCurrentNode &&
      selectedStartNode &&
      selectedDestinationNode
    ) {
      dispatch(
        fetchCargoCarPickupCargoInternodeRoute({
          id_ruta_intranod: selectedInternodeRoute?.id,
          id_nod_curent: selectedCurrentNode?.id,
          id_nod_start: selectedStartNode?.id,
          id_nod_stop: selectedDestinationNode?.id,
        }),
      );
    }
  };

  const selectedNode = useMemo(() => {
    return pickupCargoInternodeRouteData.find(
      (item) => item.id_nod_destinatie === activeCard,
    );
  }, [pickupCargoInternodeRouteData, activeCard]);

  const currentPackages = selectedNode?.colete || [];
  const currentBins = selectedNode?.binuri || [];

  return (
    <div>
      <ContentCard
        cardTitle="Ridicare marfa pe ruta internod"
        cardTitleClassName="!mb-6">
        <div className="flex flex-col items-stretch gap-4 sm:flex-row sm:flex-wrap sm:items-end">
          <Dropdown
            title="Ruta internod"
            className="min-w-0 flex-1 sm:min-w-[180px]"
            noBottomSpacing
            data={internodeRouteNodesList}
            singleValue={selectedInternodeRoute}
            setSingleValue={setSelectedInternodeRoute}
          />

          <Dropdown
            title="Nod curent"
            className="min-w-0 flex-1 sm:min-w-[180px]"
            noBottomSpacing
            data={nodesByInternodRouteList}
            singleValue={selectedCurrentNode}
            setSingleValue={setSelectedCurrentNode}
          />

          <Dropdown
            title="Nod start"
            className="min-w-0 flex-1 sm:min-w-[180px]"
            noBottomSpacing
            data={nodesByInternodRouteList}
            singleValue={selectedStartNode}
            setSingleValue={setSelectedStartNode}
          />

          <Dropdown
            title="Nod destinatie"
            className="min-w-0 flex-1 sm:min-w-[180px]"
            noBottomSpacing
            data={nodesByInternodRouteList}
            singleValue={selectedDestinationNode}
            setSingleValue={setSelectedDestinationNode}
          />

          <CustomButton
            variant="contained"
            className="!h-[48px] !min-w-[112px] shrink-0"
            onClick={handleSearch}>
            Cauta
          </CustomButton>
        </div>
      </ContentCard>

      {isLoadingPickupCargoInternodeRoute && (
        <div className="flex items-center justify-center">
          <LoadingIndicator />
        </div>
      )}

      {pickupCargoInternodeRouteData.length === 0 && (
        <ContentCard className="mt-2 text-center">
          <h2 className="text-xl font-bold text-gray-900">Nu exista marfa</h2>
        </ContentCard>
      )}

      <div>
        {pickupCargoInternodeRouteData.length > 0 &&
          !isLoadingPickupCargoInternodeRoute && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              {pickupCargoInternodeRouteData.map((item) => {
                const handleShowCargo = () => {
                  if (item.id_nod_destinatie) {
                    setActiveCard(item.id_nod_destinatie);
                  }
                };

                return (
                  <div key={item.id} className="min-w-0">
                    <ContentCard
                      isLoading={isLoadingPickupCargoInternodeRoute}
                      cardTitle={item.denumireNod}
                      className={`${
                        activeCard === item.id_nod_destinatie
                          ? "border-2 border-[#2196f3]"
                          : "border-2 border-slate-200"
                      } cursor-pointer`}
                      onClick={handleShowCargo}>
                      <div className="flex items-center justify-between border-b border-slate-200 py-2">
                        <p className="mb-0 text-base font-bold text-gray-900">
                          Binuri x{" "}
                          <span className="font-bold text-danger">
                            {item.countBinuri}
                          </span>
                        </p>

                        <ActionMenu
                          title={item.denumireNod}
                          items={[
                            {
                              id: "view-node-cargo",
                              label: "Vezi marfa",
                              icon: "PACKAGES",
                              tooltip: item.id_nod_destinatie
                                ? "Afiseaza BIN-urile si coletele pentru acest nod de destinatie."
                                : "Lipseste nodul de destinatie necesar pentru afisarea marfii.",
                              disabled: !item.id_nod_destinatie,
                              onClick: handleShowCargo,
                            },
                          ]}
                        />
                      </div>

                      <div className="flex items-center justify-between py-2">
                        <p className="mb-0 text-base font-bold text-gray-900">
                          Colete x{" "}
                          <span className="font-bold text-danger">
                            {item.countColete}
                          </span>
                        </p>
                      </div>
                    </ContentCard>
                  </div>
                );
              })}
            </div>
          )}
      </div>

      {currentBins.length > 0 && (
        <ContentCard>
          <BinsTable bins={currentBins} />
        </ContentCard>
      )}

      {currentPackages.length > 0 && (
        <ContentCard>
          <PackagesTable packages={currentPackages} />
        </ContentCard>
      )}

      <ClosePackageCargoInternodeModal
        id_ruta_intranod={selectedInternodeRoute?.id}
        id_nod_curent={selectedCurrentNode?.id}
        id_nod_start={selectedStartNode?.id}
        id_nod_stop={selectedDestinationNode?.id}
      />
    </div>
  );
}

export default PickupCargoInternodeRoute;
